// /burn-rate — Aufschluesselung der monatlichen Cash-Outflows pro Firma.
//
// Quelle: recurring_outflow_pattern (Migration 0008), aus BWA YTD Jan-Jul 2025
// + Lohnjournal Maerz 2026. Wird von KPI-Service.build_runway() konsumiert.
//
// Layout: Top-KPI (Holding-Total) → Pro-Firma-Stack-Bar mit Kategorien-Split
// → Tabelle aller Patterns.

import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  BurnRateResponse,
  RecurringPatternEntry,
  UnauthorizedError,
  fetchBurnRate,
  formatEUR,
} from "../lib/api";
import { useAuth } from "../lib/auth";

export default function BurnRatePage() {
  const auth = useAuth();
  const burnQuery = useQuery<BurnRateResponse, Error>({
    queryKey: ["recurring", "burn-rate"],
    queryFn: fetchBurnRate,
    enabled: auth.isAuthenticated,
    refetchInterval: 5 * 60_000,
    retry: (n, err) => !(err instanceof UnauthorizedError) && n < 1,
  });

  if (auth.isLoading) {
    return <FullPage>Session wird geprüft …</FullPage>;
  }
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (burnQuery.error instanceof UnauthorizedError) {
    return <Navigate to="/login" replace />;
  }

  const data = burnQuery.data;

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-md sticky top-0 z-10">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <h1 className="text-sm sm:text-base font-semibold text-slate-100 flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center min-h-11 px-2 -ml-2 text-sky-300 hover:text-sky-200"
            >
              ← Dashboard
            </Link>
            <span className="text-slate-500">·</span>
            <span>Burn-Rate (recurring outflows)</span>
          </h1>
          <span className="text-xs text-slate-300">{auth.me?.email}</span>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-6 max-w-6xl mx-auto space-y-5">
        {burnQuery.isLoading && <p className="text-slate-400 text-sm">Lade …</p>}
        {burnQuery.error && !(burnQuery.error instanceof UnauthorizedError) && (
          <p className="text-rose-300 text-sm">Fehler: {burnQuery.error.message}</p>
        )}
        {data && !data.available && (
          <p className="text-amber-300 text-sm">{data.notiz ?? "Keine Patterns geladen."}</p>
        )}

        {data && data.available && (
          <>
            {/* Top-KPI */}
            <section className="rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/[0.06] to-slate-950 p-5">
              <header className="flex items-baseline justify-between gap-2 mb-2">
                <h2 className="text-xs uppercase tracking-widest text-slate-300">
                  Holding-Burn (Whitelist xholding/drainx/gundk)
                </h2>
                <BwaAnchorBadge
                  iso={data.bwa_anchor_iso}
                  ageDays={data.bwa_anchor_age_days}
                />
              </header>
              <div className="text-4xl font-bold tabular-nums text-rose-100">
                {formatEUR(data.total_monatlich_eur)}
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Quelle: recurring_outflow_pattern (statisch). Aktuellste BWA in DB
                {data.bwa_anchor_iso
                  ? `: ${data.bwa_anchor_iso} (${data.bwa_anchor_age_days ?? "?"} T alt)`
                  : ": keine"}
                . Update via{" "}
                <code className="text-slate-500">app.stb.reparse_bwa_ytd</code> oder Pattern-Refresh.
              </p>
            </section>

            {/* Pro-Firma Stack-Bar */}
            <FirmaStackChart data={data} />

            {/* Tabellen pro Firma */}
            <div className="space-y-5">
              {data.by_firma.map((block) => (
                <FirmaTable key={block.firma_id} block={block} />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// Stack-Bar: x = firma, y = Summe, Stack-Segment = Kategorie
function FirmaStackChart({ data }: { data: BurnRateResponse }) {
  // Build chart-data: ein Object pro Firma mit allen Kategorien als Keys
  const allKategorien = Array.from(
    new Set(data.by_firma.flatMap((b) => b.by_kategorie.map((p) => p.kategorie))),
  );

  const colorFor = (kategorie: string) => {
    const ref = data.by_firma
      .flatMap((b) => b.by_kategorie)
      .find((p) => p.kategorie === kategorie);
    return ref?.kategorie_color ?? "#64748b";
  };

  const displayFor = (kategorie: string) => {
    const ref = data.by_firma
      .flatMap((b) => b.by_kategorie)
      .find((p) => p.kategorie === kategorie);
    return ref?.kategorie_display ?? kategorie;
  };

  const rows = data.by_firma.map((block) => {
    const row: Record<string, string | number> = { firma: block.firma_display };
    for (const k of allKategorien) {
      const p = block.by_kategorie.find((pp) => pp.kategorie === k);
      row[k] = p ? Number(p.betrag_monatlich_eur) : 0;
    }
    return row;
  });

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-950 p-4 sm:p-5">
      <h2 className="text-sm font-semibold text-slate-100 mb-3">
        Burn-Rate pro Firma — Stack-Aufteilung
      </h2>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
            <XAxis dataKey="firma" stroke="#94a3b8" tick={{ fill: "#94a3b8", fontSize: 12 }} />
            <YAxis
              stroke="#94a3b8"
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              tickFormatter={(v) =>
                new Intl.NumberFormat("de-DE", { notation: "compact" }).format(v)
              }
            />
            <Tooltip
              contentStyle={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 8,
              }}
              formatter={(v: number, name) => [
                new Intl.NumberFormat("de-DE", {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 0,
                }).format(v),
                displayFor(String(name)),
              ]}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, color: "#cbd5e1" }}
              formatter={(value) => displayFor(String(value))}
            />
            {allKategorien.map((k) => (
              <Bar key={k} dataKey={k} stackId="burn" fill={colorFor(k)}>
                {rows.map((_, idx) => (
                  <Cell key={idx} fill={colorFor(k)} />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function FirmaTable({ block }: { block: BurnRateResponse["by_firma"][0] }) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-950 overflow-hidden">
      <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-slate-800">
        <h3 className="text-sm font-semibold text-slate-100">{block.firma_display}</h3>
        <div className="text-base font-bold tabular-nums text-slate-100">
          {formatEUR(block.total_monatlich_eur)} <span className="text-xs text-slate-400">/Monat</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-900/40 text-slate-400 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2">Kategorie</th>
              <th className="text-right px-4 py-2">Betrag/Monat</th>
              <th className="text-center px-4 py-2">Tag</th>
              <th className="text-left px-4 py-2">Quelle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {block.by_kategorie.map((p: RecurringPatternEntry) => (
              <tr key={p.id} className="text-slate-100">
                <td className="px-4 py-2">
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-2"
                    style={{ background: p.kategorie_color }}
                  />
                  {p.kategorie_display}
                  {p.notiz && (
                    <span className="block text-xs text-slate-500 ml-4">{p.notiz}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold">
                  {formatEUR(p.betrag_monatlich_eur)}
                </td>
                <td className="px-4 py-2 text-center text-slate-400 tabular-nums">
                  {p.tag_im_monat}.
                </td>
                <td className="px-4 py-2 text-xs text-slate-500">{p.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FullPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-300 text-sm">
      {children}
    </div>
  );
}

/** T1 (#54, 2026-05-21): StaleBadge fuer BWA-Anker auf /burn-rate-Page.
 *
 * Schwellwerte:
 * - <= 30 Tage: gruen "BWA aktuell"
 * - 30-60 Tage: amber "BWA X Monate alt"
 * - > 60 Tage: rot "BWA stale (X Monate)"
 * - null: grau "kein BWA in DB"
 */
function BwaAnchorBadge({
  iso,
  ageDays,
}: {
  iso: string | null;
  ageDays: number | null;
}) {
  if (iso == null || ageDays == null) {
    return (
      <span className="text-[10px] tabular-nums px-2 py-0.5 rounded border border-slate-600/40 bg-slate-700/20 text-slate-400">
        BWA-Anker fehlt
      </span>
    );
  }
  const months = Math.round(ageDays / 30);
  let cls: string;
  let label: string;
  if (ageDays <= 30) {
    cls = "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
    label = "BWA aktuell";
  } else if (ageDays <= 60) {
    cls = "border-amber-500/40 bg-amber-500/10 text-amber-200";
    label = `BWA ${months} Monate alt`;
  } else {
    cls = "border-rose-500/40 bg-rose-500/10 text-rose-200";
    label = `BWA stale (${months} Monate)`;
  }
  return (
    <span
      className={`text-[10px] tabular-nums px-2 py-0.5 rounded border ${cls}`}
      title={`BWA-Anker: ${iso} (${ageDays} Tage alt)`}
    >
      {label}
    </span>
  );
}
